/**
 * AIFilingPage.js — Phase 2 v5
 * Migration: Cloudflare Worker removed. All calls now go to FastAPI backend.
 * Change: CF_WORKER_URL → REACT_APP_BACKEND_URL (same as rest of app via `api` instance)
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate }                  from 'react-router-dom';
import { ClientLayout }                 from '../components/ClientLayout';
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast }                        from 'sonner';
import { getCurrentFilingYear, getRecentFilingYears } from '../hooks/filingYear';
import {
  Upload, FileText, CheckCircle, AlertCircle, Clock,
  Download, ArrowRight, Sparkles, RefreshCw, Plus, X,
  ChevronDown, ChevronUp, Info, Shield, AlertTriangle,
  MessageCircle,
} from 'lucide-react';
import { Button }   from '@/components/ui/button';
import { Badge }    from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';



function getSessionFromURL() {
  return new URLSearchParams(window.location.search).get('session');
}

const DOCUMENT_TYPES = [
  { value: 'form16',               label: 'Form 16',                required: true,  allowMultiple: true,  hint: 'Upload one per employer if you changed jobs during the year' },
  { value: 'form26as',             label: 'Form 26AS / AIS',        required: true,  allowMultiple: true, hint: 'Download from incometax.gov.in — covers all employers' },
  { value: 'bank_statement',       label: 'Bank Statement',         required: false, allowMultiple: true, hint: 'Savings account statement for the financial year' },
  { value: 'salary_slip',          label: 'Salary Slips',           required: false, allowMultiple: true,  hint: 'Upload slips from each employer if you changed jobs' },
  { value: 'investment_proof_80c', label: '80C Investment Proofs',  required: false, allowMultiple: true, hint: 'LIC, ELSS, PPF, EPF statement' },
  { value: 'investment_proof_80d', label: 'Health Insurance (80D)', required: false, allowMultiple: true, hint: 'Medical insurance premium receipt' },
  { value: 'hra_rent_receipt',     label: 'Rent Receipts',          required: false, allowMultiple: true, hint: 'If you pay rent and claim HRA' },
  { value: 'capital_gains',        label: 'Capital Gains Statement',required: false, allowMultiple: true,  hint: 'Upload one per broker — Zerodha, Groww, Angel etc.' },
  { value: 'stock_statement',      label: 'Stock / MF Statement',   required: false, allowMultiple: true, hint: 'CDSL / NSDL consolidated account statement' },
  { value: 'other',                label: 'Other Document',         required: false, allowMultiple: true,  hint: 'Any other relevant document' },
];

const PROCESSING_STEPS = [
  { id: 'upload',   label: 'Documents received',           icon: Upload },
  { id: 'classify', label: 'Detecting income sources',     icon: FileText },
  { id: 'retrieve', label: 'Loading applicable tax rules', icon: Shield },
  { id: 'analyse',  label: 'AI tax analysis',              icon: Sparkles },
  { id: 'validate', label: 'Validating output',            icon: CheckCircle },
  { id: 'complete', label: 'ITR JSON ready',               icon: Download },
];

const STEP_FOR_STATUS = {
  uploading: 0, processing: 2, needs_reupload: 1, completed: 5, failed: -1,
};

export default function AIFilingPage() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const fileInputRef = useRef(null);
  const pollingRef   = useRef(null);

  const [view, setView]               = useState('loading');
  const [sessionId, setSessionId]     = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [history, setHistory]         = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  // uploadedDocs: { [docType]: [{ id, file_name, status }] }
  const [uploadedDocs, setUploadedDocs]   = useState({});
  const uploadingTypeRef = useRef(null);
  const [uploadingType, setUploadingType] = useState(null); // UI display only
  const [processingStatus, setProcessingStatus] = useState('');
  const [failureInfo, setFailureInfo]         = useState(null);

  const { financialYear: defaultFY } = getCurrentFilingYear();
  const [selectedFY, setSelectedFY] = useState(defaultFY);

  useEffect(() => {
    fetchHistory(); // fetchHistory handles URL session param internally
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const [historyLimit, setHistoryLimit] = useState(5);

  const fetchHistory = async () => {
    try {
      const res = await api.get('/ai-filing/history');
      setHistory(res.data || []);
      // Check if redirected here from a specific session (payment callback)
      const urlSess = getSessionFromURL();
      if (urlSess) {
        setSessionId(urlSess);
        setView('upload');
        window.history.replaceState({}, '', '/ai-filing');
      }
      // Do NOT auto-navigate to start view — always show explainer first
      // User explicitly clicks "Get Started" to proceed
    } catch { } finally {
      setLoadingHistory(false);
      // Show explainer if not already redirected to upload
      setView(prev => prev === 'loading' ? 'explainer' : prev);
    }
  };

  const startNewSession = async () => {
    try {
      const res = await api.post('/ai-filing/session', { financial_year: selectedFY, amount_paid: 0 });
      setSessionId(res.data.session_id);
      setUploadedDocs({});
      setView('upload');
    } catch { toast.error('Could not start filing session.'); }
  };

  const handleFileSelect = async (event) => {
    const file    = event.target.files?.[0];
    const docType = uploadingTypeRef.current; // use ref — always current value
    if (!file || !docType || !sessionId) return;
    event.target.value = '';
    const tempId  = `temp_${Date.now()}`;
    const dt      = DOCUMENT_TYPES.find(d => d.value === docType);
    const isMulti = dt?.allowMultiple;
    setUploadedDocs(prev => {
      const existing = prev[docType] || [];
      const newEntry = { id: tempId, file_name: file.name, status: 'uploading' };
      return { ...prev, [docType]: isMulti ? [...existing, newEntry] : [newEntry] };
    });
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    try {
      const res = await api.post(`/ai-filing/session/${sessionId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const allDocs = res.data.documents || [];
      const newDocsOfType = allDocs.filter(d => d.document_type === docType);
      setUploadedDocs(prev => ({
        ...prev,
        [docType]: newDocsOfType.map(d => ({ id: d.id, file_name: d.original_name, status: 'uploaded' })),
      }));
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Upload failed';
      // Password protected PDFs get a longer message — show as alert not toast
      if (errMsg.includes('password protected')) {
        toast.error('Password protected PDF — see instructions below', { duration: 5000 });
        // Store error on the temp entry so UI can show it
        setUploadedDocs(prev => {
          const existing = prev[docType] || [];
          return {
            ...prev,
            [docType]: existing.map(d =>
              d.id === tempId ? { ...d, status: 'error', error: errMsg } : d
            ),
          };
        });
      } else {
        toast.error(errMsg);
        setUploadedDocs(prev => {
          const filtered = (prev[docType] || []).filter(d => d.id !== tempId);
          if (!filtered.length) { const c = { ...prev }; delete c[docType]; return c; }
          return { ...prev, [docType]: filtered };
        });
      }
    }
    uploadingTypeRef.current = null;
    setUploadingType(null);
  };

  // Calls Worker DELETE — removes from R2 + D1
  const removeDocument = async (docType, docId) => {
    try {
      const res = await api.delete(`/ai-filing/session/${sessionId}/document/${docId}`);
      const remaining = res.data.documents || [];
      const byType = {};
      remaining.forEach(d => {
        if (!byType[d.document_type]) byType[d.document_type] = [];
        byType[d.document_type].push({ id: d.id, file_name: d.original_name, status: 'uploaded' });
      });
      setUploadedDocs(byType);
      toast.success('Document removed');
    } catch { toast.error('Failed to remove document.'); }
  };

  const startProcessing = async () => {
    const missing = DOCUMENT_TYPES.filter(d => d.required && !(uploadedDocs[d.value]?.length));
    if (missing.length > 0) { toast.error(`Please upload: ${missing.map(d => d.label).join(', ')}`); return; }
    setView('processing');
    setProcessingStatus(PROCESSING_STEPS[1].label);
    try {
      await api.post(`/ai-filing/session/${sessionId}/process`);
      pollingRef.current = setInterval(async () => {
        try {
          const res = await api.get(`/ai-filing/session/${sessionId}`);
          const s   = res.data;
          setSessionData(s);
          if (s.status === 'completed') { clearInterval(pollingRef.current); setView('completed'); fetchHistory(); }
          else if (s.status === 'failed') { clearInterval(pollingRef.current); setFailureInfo({ reason: s.error_message || 'Processing failed. Please try again.' }); setView('failed'); }
          else if (s.status === 'needs_reupload') { clearInterval(pollingRef.current); toast.warning('Some docs need re-upload.'); setView('upload'); }
          else { const step = STEP_FOR_STATUS[s.status] ?? 2; setProcessingStatus(PROCESSING_STEPS[Math.min(step+1,4)]?.label || 'Processing…'); }
        } catch { }
      }, 3000);
    } catch { toast.error('Failed to start processing.'); setView('upload'); }
  };

  const downloadITR = async (sid) => {
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const res   = await fetch(`${BACKEND_URL}/api/ai-filing/session/${sid}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || `itr_${sid.slice(0,8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('ITR JSON downloaded!');
    } catch { toast.error('Download failed.'); }
  };

  const totalUploadedCount = Object.values(uploadedDocs).reduce((s, docs) => s + (docs?.length || 0), 0);

  return (
    <ClientLayout>
      <div className="space-y-8 max-w-4xl mx-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent)' }}>
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>AI Tax Filing</h1>
                <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ backgroundColor: '#fff3e0', color: 'var(--accent)' }}>
                  BETA
                </span>
              </div>
            </div>
            <p className="opacity-60 text-sm">Upload documents · AI analyses · Download ITR JSON · File yourself</p>
          </div>
          {history.length > 0 && view !== 'history' && (
            <Button variant="outline" size="sm" onClick={() => setView('history')}>
              <Clock size={14} className="mr-1" /> AI Filing History
            </Button>
          )}
        </div>

        {view === 'loading' && <div className="flex justify-center py-20"><div className="spinner" /></div>}
        {view === 'explainer' && <ExplainerView onStart={() => setView('start')} />}
        {view === 'start' && <StartView history={history} loading={loadingHistory} selectedFY={selectedFY} setSelectedFY={setSelectedFY} onStart={startNewSession} onViewHistory={() => setView('history')} onDownload={downloadITR} />}
        {view === 'upload' && <UploadView sessionId={sessionId} uploadedDocs={uploadedDocs} onTriggerUpload={(docType) => { uploadingTypeRef.current = docType; setUploadingType(docType); fileInputRef.current?.click(); }} onRemove={removeDocument} onProcess={startProcessing} sessionData={sessionData} totalUploadedCount={totalUploadedCount} />}
        {view === 'processing' && <ProcessingView status={processingStatus} sessionData={sessionData} />}
        {view === 'completed' && sessionData && <CompletedView session={sessionData} onDownload={() => downloadITR(sessionId)} onNewFiling={() => { setView('start'); fetchHistory(); }} />}
        {view === 'failed' && <FailureView failureInfo={failureInfo} onRetry={() => { setView('upload'); setFailureInfo(null); }} onNewFiling={() => { setView('start'); setFailureInfo(null); fetchHistory(); }} />}
        {view === 'history' && <HistoryView history={history} onDownload={downloadITR} onNewFiling={() => setView('start')} />}

        <input ref={fileInputRef} type="file" className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv" onChange={handleFileSelect} />
      </div>
    </ClientLayout>
  );
}

function ExplainerView({ onStart }) {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl p-8 text-center" style={{ background: 'linear-gradient(135deg, var(--primary) 0%, #1a4d35 100%)', color: 'white' }}>
        <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-90" />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Fraunces, serif' }}>AI-Powered ITR Filing</h2>
        <p className="opacity-80 max-w-xl mx-auto text-sm leading-relaxed">Upload your tax documents. Our AI reads them, selects the best tax regime, computes your tax, and generates a ready-to-submit ITR JSON — in under 2 minutes.</p>
      </div>
      <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Info size={16} style={{ color: 'var(--accent)' }} /> What this covers</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {[
            ['✓','Best for salaried individuals (ITR-1 / ITR-2)','var(--success)'],
            ['✓','Form 16, 26AS, common deductions supported','var(--success)'],
            ['✓','Old vs New regime comparison','var(--success)'],
            ['⚠','Multi-employer (beta)','var(--accent)'],
            ['⚠','Capital gains limited support','var(--accent)'],
            ['✗','Foreign income / DTAA not supported','#888'],
          ].map(([icon,label,color]) => (
            <div key={label} className="flex items-center gap-2">
              <span style={{ color, fontWeight: 600 }}>{icon}</span>
              <span className="opacity-75">{label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl p-4 flex gap-3" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
        <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">AI-generated ITR JSON is based on your documents. Please verify all figures before submitting on incometax.gov.in.</p>
      </div>
      <Button onClick={onStart} className="w-full py-3 text-base font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
        I understand — Get Started <ArrowRight size={18} />
      </Button>
    </div>
  );
}

function StartView({ history, loading, selectedFY, setSelectedFY, onStart, onViewHistory, onDownload }) {
  const recentCompleted = history.filter(s => s.status === 'completed')[0];
  const FY_OPTIONS = getRecentFilingYears();
  return (
    <div className="space-y-5">
      <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
        <h2 className="font-semibold mb-4" style={{ fontFamily: 'Fraunces, serif' }}>Start New AI Filing</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <label className="text-sm opacity-60 block mb-1">Financial Year</label>
            <select value={selectedFY} onChange={e => setSelectedFY(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
              {FY_OPTIONS.map(fy => <option key={fy} value={fy}>{fy}</option>)}
            </select>
          </div>
          <Button onClick={onStart} className="flex items-center gap-2 mt-4" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
            <Plus size={16} /> Start Filing
          </Button>
        </div>
      </div>
      {recentCompleted && (
        <div className="rounded-xl p-5 flex items-center justify-between" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-3">
            <CheckCircle size={18} style={{ color: 'var(--success)' }} />
            <div>
              <div className="text-sm font-medium">FY {recentCompleted.financial_year} · {recentCompleted.itr_category} · {recentCompleted.regime_selected?.toUpperCase()} Regime</div>
              <div className="text-xs opacity-50">Completed {new Date(recentCompleted.processed_at).toLocaleDateString('en-IN')}</div>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => onDownload(recentCompleted.id)}><Download size={13} className="mr-1" /> Download</Button>
        </div>
      )}
      {history.length > 1 && <Button variant="ghost" onClick={onViewHistory} className="w-full text-sm opacity-60">View all {history.length} filings</Button>}
    </div>
  );
}

function UploadView({ sessionId, uploadedDocs, onTriggerUpload, onRemove, onProcess, sessionData, totalUploadedCount }) {
  const invalidDocs = sessionData?.documents?.filter(d => d.validation_status === 'invalid') || [];
  return (
    <div className="space-y-5">
      <div className="rounded-xl p-4 flex items-center justify-between" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
        <div>
          <p className="font-medium text-sm">Upload your documents</p>
          <p className="text-xs opacity-55">PDF, JPG, PNG, XLSX, CSV — max 15 MB each</p>
        </div>
        <Badge variant="outline">{totalUploadedCount} uploaded</Badge>
      </div>

      {/* Password protected PDF warning */}
      <div className="rounded-xl p-4 flex gap-3"
           style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
        <AlertTriangle size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800 leading-relaxed">
          <strong>Important:</strong> Do not upload password protected PDFs — the AI cannot read encrypted files and will return incorrect results.
          Remove passwords first via Adobe Acrobat (File → Properties → Security → No Security) or{' '}
          <a href="https://smallpdf.com/unlock-pdf" target="_blank" rel="noreferrer" className="underline">smallpdf.com/unlock-pdf</a> (free).
        </div>
      </div>

      {invalidDocs.length > 0 && (
        <div className="rounded-xl p-4 flex gap-3" style={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa' }}>
          <AlertCircle size={16} className="text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-orange-800">Re-upload required</p>
            {invalidDocs.map(d => <p key={d.id} className="text-xs text-orange-700 mt-0.5">• {d.document_type}: {d.validation_notes}</p>)}
          </div>
        </div>
      )}
      <div className="space-y-3">
        {DOCUMENT_TYPES.map(dt => {
          const uploads    = uploadedDocs[dt.value] || [];
          const hasUploads = uploads.length > 0;
          return (
            <div key={dt.value} className="rounded-xl p-4" style={{ backgroundColor: 'var(--background)', border: `1px solid ${hasUploads ? 'var(--primary)' : 'var(--border)'}` }}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{dt.label}</span>
                    {dt.required && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'var(--accent)', color: 'white', fontSize: '10px' }}>Required</span>}
                    {dt.allowMultiple && <span className="text-xs opacity-35">multiple allowed</span>}
                  </div>
                  <p className="text-xs opacity-50 mt-0.5">{dt.hint}</p>
                </div>
                {/* Show Upload button only when no files uploaded yet */}
                {!hasUploads && (
                  <button onClick={() => onTriggerUpload(dt.value)}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg flex-shrink-0"
                          style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
                    Upload
                  </button>
                )}
              </div>
              {/* Uploaded files list */}
              {hasUploads && (
                <div className="space-y-2">
                  {uploads.map(doc => (
                    <div key={doc.id}>
                      <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg"
                           style={{ backgroundColor: doc.status === 'error' ? '#fff1f2' : 'var(--secondary)',
                                    border: doc.status === 'error' ? '1px solid #fecdd3' : 'none' }}>
                        <div className="flex items-center gap-2 min-w-0">
                          {doc.status === 'uploading'
                            ? <RefreshCw size={13} className="animate-spin opacity-50 flex-shrink-0" />
                            : doc.status === 'error'
                            ? <AlertCircle size={13} className="flex-shrink-0 text-red-500" />
                            : <CheckCircle size={13} className="flex-shrink-0" style={{ color: 'var(--success)' }} />}
                          <span className={`text-xs truncate ${doc.status === 'error' ? 'text-red-700' : 'opacity-70'}`}>
                            {doc.file_name}
                          </span>
                        </div>
                        {doc.status === 'uploaded' && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => onTriggerUpload(dt.value)}
                                    className="text-xs px-2 py-0.5 rounded opacity-60 hover:opacity-100"
                                    style={{ border: '1px solid var(--border)' }}>
                              Replace
                            </button>
                            <button onClick={() => onRemove(dt.value, doc.id)}
                                    className="w-6 h-6 rounded flex items-center justify-center opacity-50 hover:opacity-100 hover:text-red-500">
                              <X size={12} />
                            </button>
                          </div>
                        )}
                        {doc.status === 'error' && (
                          <button onClick={() => setUploadedDocs(prev => {
                            const filtered = (prev[dt.value] || []).filter(d => d.id !== doc.id);
                            if (!filtered.length) { const c = {...prev}; delete c[dt.value]; return c; }
                            return { ...prev, [dt.value]: filtered };
                          })} className="w-6 h-6 rounded flex items-center justify-center opacity-50 hover:opacity-100">
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      {doc.status === 'error' && doc.error?.includes('password') && (
                        <div className="mt-1 px-3 py-2 rounded-lg text-xs text-red-700 leading-relaxed"
                             style={{ backgroundColor: '#fff1f2', border: '1px solid #fecdd3' }}>
                          <strong>Password protected.</strong> Remove password first —
                          Adobe: File → Properties → Security → No Security, or use{' '}
                          <a href="https://smallpdf.com/unlock-pdf" target="_blank" rel="noreferrer"
                             className="underline">smallpdf.com/unlock-pdf</a> (free)
                        </div>
                      )}
                    </div>
                  ))}
                  {/* + Add another — only for multi-upload types, only after first upload (Option B) */}
                  {dt.allowMultiple && (
                    <button onClick={() => onTriggerUpload(dt.value)}
                            className="flex items-center gap-1.5 text-xs opacity-60 hover:opacity-100 transition-opacity mt-1 px-1"
                            style={{ color: 'var(--primary)' }}>
                      <Plus size={13} /> Add another {dt.label}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between pt-1">
        <p className="text-sm opacity-50">{totalUploadedCount === 0 ? 'Upload Form 16 and Form 26AS to continue' : `${totalUploadedCount} file${totalUploadedCount > 1 ? 's' : ''} ready`}</p>
        <Button onClick={onProcess} disabled={totalUploadedCount === 0} className="flex items-center gap-2" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
          <Sparkles size={15} /> Analyse with AI <ArrowRight size={15} />
        </Button>
      </div>
    </div>
  );
}

function ProcessingView({ status, sessionData }) {
  const currentStep = STEP_FOR_STATUS[sessionData?.status] ?? 2;
  const progress    = Math.min(((currentStep + 1) / PROCESSING_STEPS.length) * 100, 92);
  return (
    <div className="rounded-2xl p-10 text-center space-y-7" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
      <div>
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(194,94,0,0.1)' }}>
          <Sparkles className="w-7 h-7 animate-pulse" style={{ color: 'var(--accent)' }} />
        </div>
        <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Fraunces, serif' }}>AI is processing your documents</h2>
        <p className="text-sm opacity-55">Usually takes 30–90 seconds. Keep this page open.</p>
      </div>
      <div className="space-y-2.5 max-w-xs mx-auto text-left">
        {PROCESSING_STEPS.map((step, i) => {
          const Icon = step.icon;
          const done = i < currentStep, active = i === currentStep;
          return (
            <div key={step.id} className={`flex items-center gap-3 ${i > currentStep ? 'opacity-25' : ''}`}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: done ? 'var(--success)' : active ? 'var(--accent)' : 'var(--muted)' }}>
                {done ? <CheckCircle size={14} className="text-white" /> : active ? <RefreshCw size={14} className="text-white animate-spin" /> : <Icon size={14} className="opacity-50" />}
              </div>
              <div>
                <p className="text-sm" style={active ? { color: 'var(--accent)', fontWeight: 500 } : {}}>{step.label}</p>
                {active && status && <p className="text-xs opacity-50">{status}</p>}
              </div>
            </div>
          );
        })}
      </div>
      <Progress value={progress} className="max-w-xs mx-auto h-1" />
    </div>
  );
}

function CompletedView({ session, onDownload, onNewFiling }) {
  const [showDetails, setShowDetails] = useState(false);
  const t = session.tax_summary;
  const fmt = n => n != null ? `₹${Math.abs(n).toLocaleString('en-IN')}` : '—';
  const isRefund = t?.tax_refund_or_due < 0;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, var(--success) 0%, #1b5e20 100%)' }}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center"><CheckCircle size={24} className="text-white" /></div>
          <div className="flex-1">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Fraunces, serif' }}>{session.itr_category} JSON Ready</h2>
            <p className="opacity-80 text-sm">
              {session.regime_selected?.toUpperCase()} REGIME selected
              {t && ` · Reduces tax liability by ₹${Math.abs((t.tax_old_regime||0)-(t.tax_new_regime||0)).toLocaleString('en-IN')}`}
            </p>
            {session.processed_at && session.created_at && (
              <p className="opacity-60 text-xs mt-1">⏱ Processed in {Math.round((new Date(session.processed_at) - new Date(session.created_at)) / 1000)}s</p>
            )}
          </div>
          <Button onClick={onDownload} className="bg-white font-semibold" style={{ color: 'var(--success)' }}><Download size={15} className="mr-1" /> Download</Button>
        </div>
      </div>
      {session.regime_reason && (
        <div className="rounded-xl p-4 flex gap-3" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <Sparkles size={14} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--success)' }} />
          <p className="text-sm text-green-800">{session.regime_reason}</p>
          <p className="text-xs text-green-700 opacity-75 mt-1">
            Refund depends on TDS or advance tax already paid. Tax savings do not automatically mean refund.
          </p>
        </div>
      )}
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Gross Income',     value: fmt(t.gross_income) },
            { label: 'Total Deductions', value: fmt(t.total_deductions) },
            { label: 'Tax Payable',      value: fmt(t.tax_payable) },
            { label: isRefund ? 'Refund' : 'Tax Due', value: fmt(t.tax_refund_or_due), color: isRefund ? 'var(--success)' : 'var(--warning)' },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-xl p-4 text-center" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
              <p className="text-xs opacity-50 mb-1">{label}</p>
              <p className="text-lg font-bold" style={{ fontFamily: 'Fraunces, serif', color }}>{value}</p>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
        <button onClick={() => setShowDetails(v => !v)} className="w-full flex items-center justify-between p-4 text-sm font-medium hover:bg-muted/40">
          <span>Regime comparison</span>
          {showDetails ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
        {showDetails && t && (
          <div className="p-4 grid grid-cols-2 gap-3 text-sm border-t" style={{ borderColor: 'var(--border)' }}>
            {[['Old Regime Tax', fmt(t.tax_old_regime)],['New Regime Tax', fmt(t.tax_new_regime)],['TDS Deducted', fmt(t.tds_total)],['Advance Tax', fmt(t.advance_tax)]].map(([k,v]) => (
              <div key={k} className="flex justify-between"><span className="opacity-55">{k}</span><span className="font-medium">{v}</span></div>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
        <p className="font-medium text-sm mb-2">How to file using this JSON</p>
        {['Download the ITR JSON file above','Visit incometax.gov.in → e-Filing → File Income Tax Return',`Select ${session.itr_category || 'ITR'} and upload this JSON file`,'Verify all pre-filled figures carefully','e-Verify using Aadhaar OTP, Net Banking, or DSC'].map((s,i) => (
          <div key={i} className="flex gap-2 text-sm opacity-65 mt-1"><span className="font-mono text-xs opacity-40 mt-0.5">{i+1}.</span><span>{s}</span></div>
        ))}
        <p className="text-xs opacity-35 mt-3">Disclaimer: AI-generated. Verify all figures before final submission.</p>
      </div>
      <div className="flex gap-3">
        <Button onClick={onDownload} className="flex-1 flex items-center justify-center gap-2" style={{ backgroundColor: 'var(--primary)', color: 'white' }}><Download size={15} /> Download ITR JSON</Button>
        <Button variant="outline" onClick={onNewFiling} className="flex items-center gap-2"><Plus size={15} /> New Filing</Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FAILURE VIEW — proper fallback when AI processing fails
// ─────────────────────────────────────────────────────────────
function FailureView({ failureInfo, onRetry, onNewFiling }) {
  const reason = failureInfo?.reason || 'AI processing failed.';

  // Categorise the error for user-friendly message
  const isDocumentIssue = reason.includes('unreadable') || reason.includes('re-upload') ||
                          reason.includes('password') || reason.includes('No documents');
  const isSystemIssue   = reason.includes('Claude API') || reason.includes('timeout') ||
                          reason.includes('Internal Server') || reason.includes('configured');

  const userMessage = isDocumentIssue
    ? 'One or more of your documents could not be read by the AI. Please check your documents and try again.'
    : isSystemIssue
    ? 'Our AI service is temporarily unavailable. Your documents are safe — please try again in a few minutes.'
    : 'Processing failed unexpectedly. Your documents are saved — you can retry without re-uploading.';

  return (
    <div className="space-y-5">
      {/* Error header */}
      <div className="rounded-2xl p-8 text-center"
           style={{ backgroundColor: '#fff1f2', border: '1.5px solid #fecdd3' }}>
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
             style={{ backgroundColor: '#fee2e2' }}>
          <AlertCircle className="w-7 h-7 text-red-500" />
        </div>
        <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Fraunces, serif', color: '#991b1b' }}>
          Processing Failed
        </h2>
        <p className="text-sm text-red-700 max-w-md mx-auto leading-relaxed">
          {userMessage}
        </p>
      </div>

      {/* What happened */}
      <div className="rounded-xl p-4"
           style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
        <p className="text-xs font-medium opacity-50 mb-2 uppercase tracking-wide">What went wrong</p>
        <p className="text-sm opacity-70 font-mono leading-relaxed">{reason}</p>
      </div>

      {/* Good news — documents are safe */}
      <div className="rounded-xl p-4 flex gap-3"
           style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
        <CheckCircle size={16} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--success)' }} />
        <div>
          <p className="text-sm font-medium text-green-800">Your documents are safe</p>
          <p className="text-xs text-green-700 mt-0.5">
            All uploaded documents are still saved. You do not need to re-upload them to retry.
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Button
          onClick={onRetry}
          className="flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
          <RefreshCw size={15} /> Try Again
        </Button>

        <Button
          onClick={onNewFiling}
          variant="outline"
          className="flex items-center justify-center gap-2">
          <Upload size={15} /> Re-upload Documents
        </Button>

        <a
          href="mailto:support@mooltax.com?subject=AI Filing Failed&body=Session failed. Please help."
          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium hover:opacity-80 transition-opacity"
          style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
          <MessageCircle size={15} /> Contact Support
        </a>
      </div>

      {/* Tips for common issues */}
      {isDocumentIssue && (
        <div className="rounded-xl p-5"
             style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
          <p className="font-medium text-sm mb-3">Common fixes</p>
          {[
            ['Password protected PDF', 'Remove password via Adobe Acrobat or smallpdf.com/unlock-pdf'],
            ['Scanned image PDF', 'Upload the original digital PDF from employer if available'],
            ['Corrupted file', 'Re-download the document from source and upload again'],
            ['Wrong file format', 'Only PDF, JPG, PNG, XLSX and CSV are supported'],
          ].map(([issue, fix]) => (
            <div key={issue} className="flex gap-2 text-sm mb-2">
              <span className="font-medium opacity-70 flex-shrink-0">{issue}:</span>
              <span className="opacity-55">{fix}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryView({ history, onDownload, onNewFiling }) {
  const [visibleCount, setVisibleCount] = useState(5);
  const visibleHistory = history.slice(0, visibleCount);
  const hasMore = history.length > visibleCount;

  const statusCfg = {
    completed:      { label: 'Completed',     color: 'var(--success)', icon: CheckCircle },
    processing:     { label: 'Processing',    color: 'var(--warning)', icon: RefreshCw },
    uploading:      { label: 'Uploading',     color: 'var(--primary)', icon: Upload },
    needs_reupload: { label: 'Action needed', color: '#f59e0b',       icon: AlertCircle },
    failed:         { label: 'Failed',        color: 'var(--error)',   icon: X },
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Filing History</h2>
        <Button size="sm" onClick={onNewFiling} style={{ backgroundColor: 'var(--primary)', color: 'white' }}><Plus size={13} className="mr-1" /> New Filing</Button>
      </div>
      {history.length === 0
        ? <div className="text-center py-10 opacity-35"><FileText className="w-8 h-8 mx-auto mb-2" /><p>No filings yet</p></div>
        : visibleHistory.map(s => {
            const cfg = statusCfg[s.status] || statusCfg.uploading;
            const Icon = cfg.icon;
            const t = s.tax_summary_preview;
            return (
              <div key={s.id} className="rounded-xl p-4 flex items-center gap-3" style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + '20' }}>
                  <Icon size={16} style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">FY {s.financial_year}</span>
                    {s.itr_category && <span className="text-xs opacity-50">{s.itr_category}</span>}
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: cfg.color + '20', color: cfg.color, fontSize: '10px' }}>{cfg.label}</span>
                    {s.regime_selected && <span className="text-xs opacity-40">{s.regime_selected.toUpperCase()} regime</span>}
                  </div>
                  {t && <p className="text-xs opacity-45 mt-0.5">Income ₹{(t.gross_income||0).toLocaleString('en-IN')} · Tax ₹{(t.tax_payable||0).toLocaleString('en-IN')} · {t.tax_refund_or_due < 0 ? `Refund ₹${Math.abs(t.tax_refund_or_due||0).toLocaleString('en-IN')}` : `Due ₹${(t.tax_refund_or_due||0).toLocaleString('en-IN')}`}</p>}
                  <p className="text-xs opacity-35 mt-0.5">{s.document_count} doc{s.document_count !== 1 ? 's' : ''} · {new Date(s.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}</p>
                </div>
                {s.status === 'completed' && s.has_itr_json && <Button size="sm" variant="outline" onClick={() => onDownload(s.id)}><Download size={13} /></Button>}
              </div>
            );
          })}
      {hasMore && (
        <button
          onClick={() => setVisibleCount(v => v + 5)}
          className="w-full text-sm py-2 rounded-lg border hover:bg-muted/30 transition-colors opacity-60 hover:opacity-100"
          style={{ borderColor: 'var(--border)' }}>
          Load more ({history.length - visibleCount} remaining)
        </button>
      )}
    </div>
  );
}